/**
 * Utilidades para notificar clientes por WhatsApp usando deep-link `wa.me`.
 *
 * NO usa APIs pagadas ni requiere aprobación: al llamar `openWhatsApp(...)` se
 * abre WhatsApp Web o la app instalada del usuario con el mensaje pre-escrito,
 * y solo hace falta tocar "Enviar" para completar el envío.
 */

/**
 * Normaliza un número de teléfono al formato E.164 requerido por wa.me
 * (solo dígitos, sin `+`, con código de país).
 *
 * Reglas aplicadas (por orden de prioridad):
 *  - Si el número original ya viene con `+`, se respeta su código.
 *  - Si tiene 10 dígitos y empieza por 809/829/849 → prefija "1" (RD).
 *  - Si tiene 11 dígitos y empieza por "1" → se deja como está.
 *  - Si tiene 10 dígitos y no coincide con RD → se asume "1" también (fallback).
 *  - Cualquier otra longitud se devuelve tal cual (por si es un país distinto).
 *
 * Devuelve `null` si no se puede normalizar (menos de 8 dígitos).
 */
export const normalizeWhatsAppNumber = (raw) => {
  if (!raw) return null;
  const original = String(raw).trim();
  const hasPlus = original.startsWith('+');
  const digits = original.replace(/\D/g, '');
  if (digits.length < 8) return null;

  if (hasPlus) return digits; // ya trae código de país explícito

  // Rep. Dominicana (área codes principales): 809, 829, 849
  if (digits.length === 10 && /^(809|829|849)/.test(digits)) {
    return `1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits;
  }
  if (digits.length === 10) {
    // Fallback razonable para números norteamericanos genéricos
    return `1${digits}`;
  }
  return digits;
};

/**
 * Construye el mensaje de notificación de un abono. Incluye:
 *  - Saludo con el nombre del cliente.
 *  - Monto y fecha del abono.
 *  - Lista de facturas cubiertas (número · monto aplicado · estado).
 *  - Saldo total pendiente restante.
 *  - Firma AVIPORK.
 *
 * `abono` es la respuesta de `POST /api/pagos/abono-cliente`.
 */
export const buildAbonoMessage = ({ clienteNombre, abono }) => {
  const money = (n) =>
    `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fecha = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
  };

  const nombre = clienteNombre || abono?.cliente_nombre || 'estimado cliente';
  const aplicaciones = abono?.aplicaciones || [];
  const facturasLines = aplicaciones.map((a) => {
    const estado = a.estado_pago_nuevo || '';
    const saldo = Number(a.saldo_restante || 0);
    const tail = estado === 'Pagada'
      ? '✅ Pagada'
      : `saldo ${money(saldo)} (${estado})`;
    return `• ${a.factura_numero}: ${money(a.monto_aplicado)} → ${tail}`;
  }).join('\n');

  const desc = (abono?.descripcion || '').trim();
  const partes = [
    `Hola ${nombre}, buen día 👋`,
    '',
    `Confirmamos tu pago de *${money(abono?.monto_aplicado)}* el ${fecha(abono?.fecha)}${abono?.metodo_pago ? ` (${abono.metodo_pago})` : ''}.`,
  ];
  if (facturasLines) {
    partes.push('', '📋 *Facturas cubiertas:*', facturasLines);
  }
  partes.push('', `💰 *Saldo pendiente:* ${money(abono?.saldo_total_restante_cliente || 0)}`);
  if (desc) partes.push('', `_${desc}_`);
  partes.push('', 'Gracias por tu pago 🙏', '— AVIPORK');

  return partes.join('\n');
};

/**
 * Abre WhatsApp (web o app instalada) con el mensaje pre-escrito para el
 * número dado. Devuelve `true` si se pudo abrir; `false` si el número no es
 * válido y por lo tanto no se abrió nada.
 */
export const openWhatsApp = ({ telefono, mensaje }) => {
  const num = normalizeWhatsAppNumber(telefono);
  if (!num) return false;
  const encoded = encodeURIComponent(mensaje || '');
  const url = `https://wa.me/${num}?text=${encoded}`;
  // Usar noopener/noreferrer y target _blank para no perder la sesión actual
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Atajo: notificar directamente el abono al cliente.
 */
export const notifyAbonoOnWhatsApp = ({ cliente, abono }) => {
  const mensaje = buildAbonoMessage({
    clienteNombre: cliente?.nombre,
    abono,
  });
  return openWhatsApp({
    telefono: cliente?.telefono || cliente?.whatsapp || cliente?.celular,
    mensaje,
  });
};
